import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LanguageCode, Profile, validateVideoAd, VideoAd } from "../../models";
import { createVideoAd, getLanguageCodes, getVideoAd, updateVideoAd } from "../../api";
import { getProfiles } from "../../api/profiles";
import Header from "../../header/header";
import { Button, LinearProgress } from "@mui/material";
import { VideoAdForm } from "../components/VideoAdForm";

export const VideoAdDetailPage: React.FC = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Data state
  const [videoAd, setVideoAd] = useState<Omit<VideoAd, "id" | "createdAt" | "updatedAt"> | null>(null);
  const [languageCodes, setLanguageCodes] = useState<LanguageCode[] | null>(null);
  const [profiles, setProfiles] = useState<Profile[] | null>(null);
  const [error, setError] = useState<any | null>(null);

  // Form
  const [videoAdErrors, setVideoAdErrors] = useState<Record<keyof VideoAd, string>>();
  const [dirty, setDirty] = useState(false);

  const loading = useMemo(() => !(videoAd && languageCodes && profiles) && !error, [videoAd, languageCodes, profiles, error]);

  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setError(null);

    try {
      if (id === "new") {
        const [languageCodes, profiles] = await Promise.all([
          await getLanguageCodes(),
          await getProfiles(),
        ]);

        setVideoAd({
          name: "",
          title: "",
          contentUrl: "",
          skippableAfterSeconds: 0,
          skippable: false,
          muted: false,
          type: "pre_roll",
          viewabilityTracking: true,
          processed: false,
          status: "active",
          profileId: 1,
          languageCodeId: 1,
        });
        setLanguageCodes(languageCodes);
        setProfiles(profiles);
      } else {
        const [video, languageCodes, profiles] = await Promise.all([
          await getVideoAd(Number(id)),
          await getLanguageCodes(),
          await getProfiles(),
        ]);

        setVideoAd(video);
        setLanguageCodes(languageCodes);
        setProfiles(profiles);
      }
    } catch (error) {
      setError(error);
    }
  }, [id]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function validate() {
    const errors = validateVideoAd(videoAd!);

    if (Object.keys(errors).length > 0) {
      setVideoAdErrors(errors as Record<keyof VideoAd, string>);
    } else {
      setVideoAdErrors(undefined);
    }

    return Object.keys(errors).length === 0;
  }

  function handleVideoAdChange(video: Omit<VideoAd, "id" | "createdAt" | "updatedAt">) {
    if (dirty) {
      validate();
    }

    setVideoAd(video);
  }

  async function handleVideoAdSave() {
    if (submitting) {
      return;
    }

    if (!validate()) {
      setDirty(true);
      return;
    }

    setDirty(false);

    enqueueSnackbar("Saving...", { variant: "info" });

    setSubmitting(true);

    try {
      if (id === "new") {
        const result = await createVideoAd(videoAd!);
        enqueueSnackbar("Video ad created", { variant: "success" });
        navigate(`/content/new-video-ads/${result.id}`);
      } else {
        await updateVideoAd(Number(id), videoAd!);
        enqueueSnackbar("Video ad saved", { variant: "success" });
        await fetchData();
      }
    } catch (e) {
      enqueueSnackbar("Failed to save video", { variant: "error" });
      console.error(e);
    }

    setSubmitting(false);
  }

  return (
    <>
      <Header />
      {loading && <div style={{ width: "50%" }}>
        <LinearProgress />
      </div>}
      {error && <div>Error: {error}</div>}
      {!loading && !error && <div className="social">
        <div className="box post-edit">
          <VideoAdForm
            videoAd={videoAd!}
            onVideoAdChange={handleVideoAdChange}
            languageCodes={languageCodes!}
            profiles={profiles!}
            errors={videoAdErrors}
          />
        </div>
        <div className="links">
          <Link to="/content/new-video-ads">
            <Button
              disabled={submitting}
              variant="contained"
              color="primary"
              className="spacer"
            >
              Back
            </Button>
          </Link>
          <Button
            disabled={submitting}
            variant="contained"
            color="primary"
            onClick={handleVideoAdSave}
          >
            Save
          </Button>
        </div>
      </div>}
    </>
  );
};
